/**
 * Anchor billing helpers (client side).
 *
 * The billing anchor is always the 1st of the month at 12:00 UTC. If today is
 * already on/after the 1st, the anchor rolls forward to the next month. This
 * mirrors the logic in the `apply-anchor-billing` / `create-payment` edge
 * functions so the date previewed in the UI matches what is applied in Stripe.
 *
 * Proration matches Stripe's create_prorations behavior: amount is scaled by
 * (seconds until anchor) / (seconds in one full billing period after the anchor).
 */
const ANCHOR_BILLING_DAY = 1;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Returns the next 1st-of-month anchor as a Date (UTC). */
export const getNextFirstOfMonthDate = (): Date => {
  const now = new Date();
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth();

  if (now.getUTCDate() >= ANCHOR_BILLING_DAY) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  return new Date(Date.UTC(year, month, ANCHOR_BILLING_DAY, 12, 0, 0));
};

/** Formats an anchor date for display in emails, e.g. "July 1, 2026". */
export const formatAnchorDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

/** Convenience: formatted next 1st-of-month date string. */
export const getNextFirstOfMonthLabel = (): string =>
  formatAnchorDate(getNextFirstOfMonthDate());

const addMonthsUtc = (date: Date, months: number): Date => {
  let year = date.getUTCFullYear();
  let month = date.getUTCMonth() + months;
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  while (month > 11) {
    month -= 12;
    year += 1;
  }
  while (month < 0) {
    month += 12;
    year -= 1;
  }

  return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
};

/**
 * Stripe-style proration factor for a future billing_cycle_anchor.
 * factor = time_until_anchor / length_of_one_full_billing_period
 */
export const getAnchorProrationFactor = (
  anchorDay: number = ANCHOR_BILLING_DAY
): { factor: number; daysRemaining: number; periodDays: number; anchorDate: Date } => {
  const nowMs = Date.now();

  let anchorDate: Date;
  if (anchorDay === ANCHOR_BILLING_DAY) {
    anchorDate = getNextFirstOfMonthDate();
  } else {
    const now = new Date();
    const currentDay = now.getUTCDate();
    let year = now.getUTCFullYear();
    let month = now.getUTCMonth();
    if (currentDay >= anchorDay) {
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
    anchorDate = new Date(Date.UTC(year, month, anchorDay, 12, 0, 0));
  }

  // One full monthly period after the anchor (Stripe price interval duration)
  const periodEnd = addMonthsUtc(anchorDate, 1);
  const periodMs = Math.max(periodEnd.getTime() - anchorDate.getTime(), 1);
  const remainingMs = Math.max(anchorDate.getTime() - nowMs, 0);
  const factor = remainingMs / periodMs;

  return {
    factor,
    daysRemaining: remainingMs / MS_PER_DAY,
    periodDays: periodMs / MS_PER_DAY,
    anchorDate,
  };
};

/**
 * Prorate a dollar amount the way Stripe does for billing_cycle_anchor
 * (down to the second, rounded to the nearest cent).
 */
export const calculateProratedAmount = (
  baseAmount: number,
  anchorDay: number = ANCHOR_BILLING_DAY
): { proratedAmount: number; daysRemaining: number; factor: number } => {
  const { factor, daysRemaining } = getAnchorProrationFactor(anchorDay);
  const proratedAmount = Math.round(baseAmount * factor * 100) / 100;

  return {
    proratedAmount,
    daysRemaining: Math.round(daysRemaining),
    factor,
  };
};
